import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  z-index: 9999;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  transition: opacity 0.3s ease;
`;

const PopupContainer = styled.div`
  width: 900px;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  background-color: white;
  border-radius: 15px;
  position: relative;
  transform: ${({ $isOpen }) => ($isOpen ? 'scale(1)' : 'scale(0.9)')};
  transition: transform 0.3s ease;
  
  /* Стилизация скроллбара - делаем его тоньше и незаметнее */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
  
  scrollbar-width: thin;
  scrollbar-color: #888 #f1f1f1;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  width: 30px;
  height: 30px;
  background: none;
  border: none;
  cursor: pointer;
  z-index: 10;
  
  svg {
    width: 23px;
    height: 23px;
    
    rect {
      fill: white;
    }
  }
  
  @media (max-width: 560px) {
    svg rect {
      fill: #ffffff;
    }
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
`;

const GallerySection = styled.div`
  background-color: #f5f5f5;
  position: relative;
  width: 100%;
  overflow: hidden; /* Добавляем overflow hidden для контейнера галереи */
`;

const SliderContainer = styled.div`
  width: 100%;
  overflow: hidden;
  position: relative;
  aspect-ratio: 16 / 9;
`;

const SliderWrapper = styled.div`
  display: flex;
  transition: transform 0.3s ease;
  transform: translateX(${({ $translateX }) => `-${$translateX}px`});
  height: 100%;
  will-change: transform;
`;

const Slide = styled.div`
  flex: 0 0 auto;
  width: 100%;
  height: 100%;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const ArrowButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
  
  &:hover {
    background-color: #f0f0f0;
  }
  
  &.left {
    left: 10px;
  }
  
  &.right {
    right: 10px;
  }
  
  svg {
    width: 7px;
    height: 13px;
    stroke: #222222;
    stroke-width: 1;
    fill: none;
  }
`;

const DotsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 10px 0;
`;

const Dot = styled.button`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background-color: ${({ $active }) => ($active ? '#222' : '#ccc')};
  cursor: pointer;
  padding: 0;
  
  &:hover {
    background-color: ${({ $active }) => ($active ? '#222' : '#999')};
  }
`;

const InfoSection = styled.div`
  padding: 30px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 20px;
  
  strong {
    font-weight: 700;
  }
`;

const Description = styled.div`
  font-size: 16px;
  line-height: 1.6;
  color: #333;
  
  p {
    margin-bottom: 15px;
  }
  
  strong {
    font-weight: 600;
  }
  
  .quill-table__wrapper {
    overflow-x: auto;
    margin: 20px 0;
  }
  
  table {
    width: 100%;
    border-collapse: collapse;
    
    td {
      border: 1px solid #ddd;
      padding: 10px;
      vertical-align: top;
    }
  }
`;

const BottomButton = styled.div`
  margin-top: 30px;
  text-align: center;
`;

const SubmitButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 40px;
  background: linear-gradient(0deg, #F6C45D 0%, #FFF093 100%);
  border: none;
  border-radius: 7px;
  color: #212121;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
`;

const TourPopup = ({ isOpen, onClose, tourData, onBookClick }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slideWidth, setSlideWidth] = useState(0);
  const sliderContainerRef = useRef(null);
  const sliderWrapperRef = useRef(null);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
      setCurrentSlide(0);
      
      // Обновляем ширину слайда при открытии и при изменении размера окна
      updateSlideWidth();
      window.addEventListener('resize', updateSlideWidth);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
      window.removeEventListener('resize', updateSlideWidth);
    };
  }, [isOpen, onClose]);

  // Функция для обновления ширины слайда
  const updateSlideWidth = () => {
    if (sliderContainerRef.current) {
      const containerWidth = sliderContainerRef.current.offsetWidth;
      setSlideWidth(containerWidth);
    }
  };

  // Обновляем ширину при изменении контента или данных
  useEffect(() => {
    if (isOpen && tourData) {
      setTimeout(updateSlideWidth, 100); // Небольшая задержка для корректного расчета
    }
  }, [isOpen, tourData]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  
  const handleBookRequest = () => {
    if (onBookClick) {
      onBookClick(tourData);
    }
    onClose();
  };

  const nextSlide = () => {
    if (tourData && tourData.gallery_images && tourData.gallery_images.length > 0) {
      setCurrentSlide((prev) => (prev + 1) % tourData.gallery_images.length);
    }
  };

  const prevSlide = () => {
    if (tourData && tourData.gallery_images && tourData.gallery_images.length > 0) {
      setCurrentSlide((prev) => (prev - 1 + tourData.gallery_images.length) % tourData.gallery_images.length);
    }
  };

  if (!tourData) return null;

  const images = tourData.gallery_images && tourData.gallery_images.length > 0 
    ? tourData.gallery_images 
    : tourData.image_url ? [tourData.image_url] : [];

  // Вычисляем смещение слайдера
  const translateX = currentSlide * slideWidth;

  return (
    <PopupOverlay $isOpen={isOpen} onClick={handleOverlayClick}>
      <PopupContainer $isOpen={isOpen}>
        <CloseButton onClick={onClose}>
          <svg viewBox="0 0 23 23">
            <g stroke="none" strokeWidth="1" fill="#fff" fillRule="evenodd">
              <rect transform="translate(11.313708, 11.313708) rotate(-45.000000) translate(-11.313708, -11.313708)" x="10.3137085" y="-3.6862915" width="2" height="30"></rect>
              <rect transform="translate(11.313708, 11.313708) rotate(-315.000000) translate(-11.313708, -11.313708)" x="10.3137085" y="-3.6862915" width="2" height="30"></rect>
            </g>
          </svg>
        </CloseButton>
        
        <Content>
          {images.length > 0 && (
            <GallerySection>
              <SliderContainer ref={sliderContainerRef}>
                <SliderWrapper 
                  ref={sliderWrapperRef}
                  $translateX={translateX}
                >
                  {images.map((img, index) => (
                    <Slide key={index} style={{ width: slideWidth ? `${slideWidth}px` : '100%' }}>
                      <img src={img} alt={`${tourData.title} - ${index + 1}`} />
                    </Slide>
                  ))}
                </SliderWrapper>
                
                {images.length > 1 && (
                  <>
                    <ArrowButton className="left" onClick={prevSlide}>
                      <svg viewBox="0 0 7.3 13">
                        <polyline points="0.5,0.5 6.5,6.5 0.5,12.5" />
                      </svg>
                    </ArrowButton>
                    <ArrowButton className="right" onClick={nextSlide}>
                      <svg viewBox="0 0 7.3 13">
                        <polyline points="0.5,0.5 6.5,6.5 0.5,12.5" />
                      </svg>
                    </ArrowButton>
                  </>
                )}
              </SliderContainer>
              
              {images.length > 1 && (
                <DotsContainer>
                  {images.map((_, index) => (
                    <Dot
                      key={index}
                      $active={currentSlide === index}
                      onClick={() => setCurrentSlide(index)}
                    />
                  ))}
                </DotsContainer>
              )}
            </GallerySection>
          )}
          
          <InfoSection>
            <Title dangerouslySetInnerHTML={{ __html: tourData.title }} />
            {tourData.detailed_description && (
              <Description dangerouslySetInnerHTML={{ __html: tourData.detailed_description }} />
            )}
            
            <BottomButton>
              <SubmitButton onClick={handleBookRequest}>Оставить заявку</SubmitButton>
            </BottomButton>
          </InfoSection>
        </Content>
      </PopupContainer>
    </PopupOverlay>
  );
};

export default TourPopup;